import styled from '@emotion/styled';

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 8px 4px 24px;
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: #1a1f36;
`;

export const PageSubtitle = styled.p`
  margin: 6px 0 0;
  font-size: 15px;
  color: #5b6477;
`;

export const Card = styled.div`
  background: #fff;
  border: 1px solid #e4e8f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
`;

export const CardBody = styled.div`
  padding: 0;
`;

export const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: #5b6477;

  strong {
    display: block;
    margin-bottom: 8px;
    color: #1a1f36;
    font-size: 16px;
  }
`;

export const TableWrap = styled.div`
  overflow-x: auto;
`;

export const SortBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 16px;
  border-bottom: 1px solid #eef1f6;
  background: #fcfdfe;
`;

export const SortLabel = styled.span`
  font-size: 13px;
  color: #6b7385;
  margin-right: 4px;
`;

export const SortChip = styled.button<{ active: boolean }>`
  border: 1px solid ${({ active }) => (active ? '#c7d7fe' : '#d7dde7')};
  background: ${({ active }) => (active ? '#eef4ff' : '#fff')};
  color: ${({ active }) => (active ? '#3559c7' : '#3d4f6f')};
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: #c5cedd;
    background: ${({ active }) => (active ? '#e4edff' : '#f8fafc')};
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th {
    padding: 12px 16px;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #6b7385;
    background: #f8fafc;
    border-bottom: 1px solid #e4e8f0;
    white-space: nowrap;
    vertical-align: bottom;
  }

  td {
    padding: 14px 16px;
    vertical-align: top;
    border-bottom: 1px solid #eef1f6;
    color: #1a1f36;
  }

  .col-team {
    width: 34%;
  }

  .col-owners {
    width: 26%;
  }

  .col-members {
    width: 11%;
  }

  .col-status {
    width: 15%;
  }

  .col-actions {
    width: 14%;
  }

  .align-right {
    text-align: right;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: #fafbfd;
  }
`;

export const CellContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  min-height: 28px;
`;

export const CompactCellContent = styled(CellContent)`
  padding-top: 2px;
`;

export const ActionsCellContent = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  padding-top: 2px;
`;

export const TeamName = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: #1a1f36;
`;

export const MetaLine = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: #6b7385;
  line-height: 1.4;
`;

export const PillGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const Pill = styled.span<{ tone?: 'neutral' | 'accent' | 'success' | 'muted' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  background: ${({ tone }) => {
    switch (tone) {
      case 'accent':
        return '#eef4ff';
      case 'success':
        return '#ecfdf3';
      case 'muted':
        return '#f3f5f8';
      default:
        return '#f3f5f8';
    }
  }};
  color: ${({ tone }) => {
    switch (tone) {
      case 'accent':
        return '#3559c7';
      case 'success':
        return '#067647';
      case 'muted':
        return '#6b7385';
      default:
        return '#3d4f6f';
    }
  }};
`;

export const ActionGroup = styled.div`
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

export const TextButton = styled.button`
  border: 1px solid #d7dde7;
  background: #fff;
  color: #1a1f36;
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: #f8fafc;
    border-color: #c5cedd;
  }
`;

export const PrimaryButton = styled.button<{ disabled?: boolean }>`
  border: none;
  background: #3559c7;
  color: #fff;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  &:hover:not(:disabled) {
    background: #2d4bb0;
  }
`;

export const ModalShell = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
`;

export const ModalHeader = styled.div`
  padding: 20px 24px 0;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #1a1f36;
`;

export const ModalSubtitle = styled.p`
  margin: 6px 0 0;
  font-size: 14px;
  color: #6b7385;
`;

export const ModalBody = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px 20px;
  border-top: 1px solid #eef1f6;
`;

export const SecondaryButton = styled.button`
  border: 1px solid #d7dde7;
  background: #fff;
  color: #1a1f36;
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }
`;

export const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;

  & + & {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #eef1f6;
  }
`;

export const FormSectionTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: #1a1f36;
`;

export const FormSectionHint = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
  color: #6b7385;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const FormFullWidth = styled.div`
  grid-column: 1 / -1;
`;

export const MemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const MemberRow = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  text-decoration: none;
  color: inherit;
  transition: background 0.15s ease;

  &:hover {
    background: #f3f5f8;
  }
`;

export const MemberAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #dbe4ff, #eef4ff);
  color: #3559c7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
`;

export const MemberName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1a1f36;
`;

export const MutedText = styled.p`
  margin: 0;
  color: #6b7385;
  font-size: 14px;
`;

export const ErrorText = styled.p`
  margin: 0;
  color: #b42318;
  font-size: 14px;
`;

export const formatGeo = (geo?: string | null) => (geo ? geo.replace(/_/g, ' ') : null);

export const initialsForName = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

export const buildTeamMeta = (team: {
  geo?: string | null;
  region?: { name: string } | null;
  teamType?: { name: string } | null;
  parent?: { name: string } | null;
}) => {
  const parts = [
    formatGeo(team.geo),
    team.region?.name,
    team.teamType?.name,
    team.parent ? `Parent: ${team.parent.name}` : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' · ') : 'No location or hierarchy set';
};
